import React, { useCallback, useState } from 'react';
import Button from '@atlaskit/button/new';
import { Box, Flex, Stack, xcss } from '@atlaskit/primitives';
import { useIntl } from '@atlassian/jira-intl';
import { getFuzzyScale, FuzzyScale } from '../../common/utils/date/date-picker';
import {
	getLastUsedDateFieldScaleFromStorage,
	setLastUsedDateFieldScaleToStorage,
} from '../../common/utils/storage';
import type { DatePickerValue, PickerProps } from '../../types';
import { DayPicker } from './day-picker';
import { messages } from './messages';
import { MonthPicker } from './month-picker';
import { QuarterPicker } from './quarter-picker';

const FuzzyDatePickerContent = ({ withTimeScale = true, ...props }: PickerProps) => {
	const { value: date, localStorageKey } = props;
	const initialScale = getFuzzyScale(date);
	const lastUsedScale = getLastUsedDateFieldScaleFromStorage(localStorageKey);
	const [scale, setScale] = useState((!date && lastUsedScale) || initialScale);

	const { formatMessage } = useIntl();

	const onChange = useCallback(
		(newDate?: DatePickerValue) => {
			if (newDate) {
				props.onChange(newDate);
			}
		},
		[props],
	);

	const onScaleChange = useCallback(
		(newScale: FuzzyScale) => {
			setScale(newScale);
			setLastUsedDateFieldScaleToStorage(newScale, localStorageKey);
		},
		[localStorageKey],
	);
	const dateValue = date ? date.end : undefined;

	return (
		<Box xcss={datePickerContentWrapper}>
			{scale && withTimeScale && (
				<Box padding="space.200">
					<Flex direction="row" wrap="nowrap" justifyContent="stretch" gap="space.050">
						<Stack grow="fill">
							<Button
								isSelected={scale === FuzzyScale.DAY}
								onClick={() => onScaleChange(FuzzyScale.DAY)}
								shouldFitContainer
							>
								{formatMessage(messages.dayPickerButtonMessage)}
							</Button>
						</Stack>
						<Stack grow="fill">
							<Button
								isSelected={scale === FuzzyScale.MONTH}
								onClick={() => onScaleChange(FuzzyScale.MONTH)}
								shouldFitContainer
							>
								{formatMessage(messages.monthPickerButtonMessage)}
							</Button>
						</Stack>
						<Stack grow="fill">
							<Button
								isSelected={scale === FuzzyScale.QUARTER}
								onClick={() => onScaleChange(FuzzyScale.QUARTER)}
								shouldFitContainer
							>
								{formatMessage(messages.quarterPickerButtonMessage)}
							</Button>
						</Stack>
					</Flex>
				</Box>
			)}

			{scale === FuzzyScale.DAY && <DayPicker date={dateValue} onSelect={onChange} />}
			{scale === FuzzyScale.MONTH && (
				<MonthPicker date={dateValue} onSelect={onChange} withTimeScale={withTimeScale} />
			)}
			{scale === FuzzyScale.QUARTER && <QuarterPicker date={dateValue} onSelect={onChange} />}
		</Box>
	);
};

export default FuzzyDatePickerContent;

const datePickerContentWrapper = xcss({
	width: '322px',
});
